import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Observable, of, forkJoin } from 'rxjs';
import { map, tap, shareReplay } from 'rxjs/operators';
import { MaterialModule } from '../../../shared/material.module';
import { UserService } from '../../../services/user.service';
import { AppUserBase } from '../../../models/appUserBase';
import { LargeShareholderCategory } from '../../../models/largeShareholderCategory';
import { LutService } from '../../../services/lut.service';
import { LargeShareholderSearchFilter } from '../../../models/largeShareholderSearchFilter';
import { CountryFlat } from '../../../models/country';
import { Helpers } from '../../../utilities/helpers';
import { REGIONS, RegionOption } from '../../../utilities/regions';
import { TextNormalizeDirective } from '../../../directives/text-normalize.directive';

export interface LargeShareholderSearchFieldsConfig {
  showRegion?: boolean;
  showCode?: boolean;
  showName?: boolean;
  showCategory?: boolean;
  showSubCategory?: boolean;
  showCountry?: boolean;
  showYear?: boolean;
  showStartYear?: boolean;
  showUser?: boolean;
}

@Component({
  selector: 'app-large-shareholder-search-box',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    TextNormalizeDirective
],
  templateUrl: './large-shareholder-search-box.component.html',
  styleUrl: './large-shareholder-search-box.component.scss'
})
export class LargeShareholderSearchBoxComponent implements OnInit, OnChanges {
  @Input() isSearchVisible: boolean = false;
  @Input() initialFilter: LargeShareholderSearchFilter = {};
  @Input() fieldsConfig: LargeShareholderSearchFieldsConfig = {
    showRegion: true,
    showCountry: true,
    showCode: true,
    showName: true,
    showCategory: true,
    showSubCategory: true,
    showYear: true,
    showStartYear: true,
    showUser: true
  };

  regions: RegionOption[] = REGIONS;
  years: string[] = [];

  readonly EMPTY_CATEGORY_ID = '00000000-0000-0000-0000-000000000000';

  @Output() largeShareholderSearchFilter = new EventEmitter<LargeShareholderSearchFilter>();
  @Output() showOwnershipsChange = new EventEmitter<boolean>();

  showOwnerships = false;

  categories$!: Observable<LargeShareholderCategory[] | null>;
  subCategories$: Observable<LargeShareholderCategory[]> = of([]);
  allCategories: LargeShareholderCategory[] = [];
  countries$!: Observable<CountryFlat[]>;
  allCountries: CountryFlat[] = [];
  users$!: Observable<AppUserBase[]>;

  searchForm: LargeShareholderSearchFilter = {
    code: undefined,
    name: undefined,
    categoryId: undefined,
    subCategoryId: undefined,
    countryId: undefined,
    region: undefined,
    year: undefined,
    startYear: undefined,
    createdBy: undefined
  };

  constructor(private lutService: LutService, private userService: UserService) {
    this.years = Helpers.measurements();
  }

  ngOnInit(): void {
    // Load categories observable with shareReplay to avoid multiple HTTP requests
    const categoriesData$ = this.lutService.getLargeShareholderCategories().pipe(
      tap(categories => {
        this.allCategories = categories;
      }),
      shareReplay(1)
    );

    // Load countries observable with shareReplay to avoid multiple HTTP requests
    const countriesData$ = this.lutService.getVisibleCountries().pipe(
      tap(countries => {
        this.allCountries = countries;
      }),
      shareReplay(1)
    );

    // Wait for both categories and countries to load before populating the search form
    forkJoin({
      categories: categoriesData$,
      countries: countriesData$
    }).subscribe(() => {
      this.populateSearchForm();
    });

    // Set up the observables for the template
    this.categories$ = categoriesData$.pipe(
      map(categories => categories
        .filter(category => category.parentId === null)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      )
    );

    this.countries$ = countriesData$.pipe(
      map(countries => countries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })))
    );

    // Load users
    this.users$ = this.userService.getUsers(1, 100, 'FullName', true).pipe(
      map(response => response.data)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialFilter'] && !changes['initialFilter'].firstChange) {
      this.populateSearchForm();
    }
  }

  private populateSearchForm(): void {
    if (this.initialFilter && Object.keys(this.initialFilter).length > 0) {
      this.searchForm = {
        code: this.initialFilter.code,
        name: this.initialFilter.name,
        categoryId: this.initialFilter.categoryId,
        subCategoryId: this.initialFilter.subCategoryId,
        countryId: this.initialFilter.countryId,
        region: this.initialFilter.region,
        year: this.initialFilter.year,
        startYear: this.initialFilter.startYear,
        createdBy: this.initialFilter.createdBy
      };

      // Populate subcategories if categoryId is a real category (not the synthetic Empty marker)
      if (this.initialFilter.categoryId && this.initialFilter.categoryId !== this.EMPTY_CATEGORY_ID) {
        this.subCategories$ = of(
          this.allCategories
            .filter(category => category.parentId === this.initialFilter.categoryId)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        );
      }

      // Filter countries if region is present
      if (this.initialFilter.region) {
        this.countries$ = of(
          this.allCountries
            .filter(country => country.region === this.initialFilter.region)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        );
      }
    }
  }

  onRegionChange(): void {
    if (this.searchForm.region) {
      this.countries$ = of(
        this.allCountries
          .filter(country => country.region === this.searchForm.region)
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      );
    } else {
      this.countries$ = of(
        this.allCountries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      );
      this.searchForm.countryId = undefined;
    }
  }

  onCategoryChange(): void {
    if (this.searchForm.categoryId && this.searchForm.categoryId !== this.EMPTY_CATEGORY_ID) {
      this.subCategories$ = of(
        this.allCategories
          .filter(category => category.parentId === this.searchForm.categoryId)
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      );
    } else {
      this.subCategories$ = of([]);
      this.searchForm.subCategoryId = undefined;
    }
  }

  onSearch(): void {
    this.largeShareholderSearchFilter.emit({ ...this.searchForm });
  }

  onShowOwnershipsChange(): void {
    this.showOwnershipsChange.emit(this.showOwnerships);
  }

  clearSearch(): void {
    this.searchForm = {
      code: undefined,
      name: undefined,
      categoryId: undefined,
      subCategoryId: undefined,
      countryId: undefined,
      region: undefined,
      year: undefined,
      startYear: undefined,
      createdBy: undefined
    };
    this.subCategories$ = of([]);
    this.countries$ = of(
      this.allCountries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    );
    this.largeShareholderSearchFilter.emit({ ...this.searchForm });
  }
}
